/**
 * Geldbetraege leben in diesem Projekt ausschliesslich als ganzzahlige Cent.
 *
 * Der Grund ist nicht Pedanterie: die beiden Quellsysteme liefern Betraege in
 * unterschiedlichen Formaten (open-banking.io als Decimal-String "1234.56",
 * MOCO als JSON-Zahl 1234.56), und sobald irgendwo im Abgleich ein Float
 * entsteht, sind centgenaue Vergleiche kaputt — und genau darauf beruht die
 * gesamte Matching-Engine.
 */
/** Fehler beim Parsen eines Betrags aus einer der beiden APIs. */
export class AmountParseError extends Error {
    input;
    constructor(input) {
        super(`Betrag nicht interpretierbar: ${JSON.stringify(input)}`);
        this.input = input;
        this.name = "AmountParseError";
    }
}
/**
 * Wandelt eine JSON-Zahl in ihre kuerzeste dezimale Darstellung.
 *
 * Das ist bewusst `String(n)` und keine Rundung: eine aus JSON geparste Zahl
 * ist bereits der naechstliegende Double zum urspruenglichen Dezimalliteral,
 * und `String()` liefert dafuer garantiert die kuerzeste Dezimalzahl, die
 * wieder auf denselben Double zurueckfuehrt. Aus 1234.56 wird also "1234.56"
 * und nicht "1234.5599999999999".
 */
function numberToDecimalString(n) {
    if (!Number.isFinite(n))
        throw new AmountParseError(n);
    const s = String(n);
    // Exponentialschreibweise kommt bei Geldbetraegen praktisch nicht vor,
    // waere hier aber ein stiller Datenfehler — also lieber laut scheitern.
    if (s.includes("e") || s.includes("E")) {
        const fixed = n.toFixed(20).replace(/0+$/, "").replace(/\.$/, "");
        return fixed;
    }
    return s;
}
/**
 * Parst einen Betrag zu ganzzahligen Cent — ohne den Umweg ueber Float.
 *
 * Akzeptiert `"1234.56"`, `"1.234,56"`, `"-42"`, `1234.56`, `null`.
 * Gerundet wird kaufmaennisch (half away from zero) auf der Ziffernfolge
 * selbst, damit `"0.005"` deterministisch zu 1 Cent wird.
 */
export function toCents(value) {
    if (value === null || value === undefined)
        return 0;
    if (typeof value === "number")
        return decimalStringToCents(numberToDecimalString(value));
    const trimmed = value.trim();
    if (trimmed === "")
        return 0;
    return decimalStringToCents(trimmed);
}
function decimalStringToCents(input) {
    let s = input.replace(/\s/g, "");
    // Deutsche Schreibweise "1.234,56" → "1234.56". Ein Komma ist immer das
    // Dezimaltrennzeichen; Punkte sind dann Tausendertrenner.
    if (s.includes(","))
        s = s.replace(/\./g, "").replace(",", ".");
    const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(s);
    if (!m || (m[2] === "" && (m[3] ?? "") === ""))
        throw new AmountParseError(input);
    const sign = m[1] === "-" ? -1 : 1;
    const whole = m[2] === "" ? "0" : m[2];
    const frac = m[3] ?? "";
    const centDigits = (frac + "00").slice(0, 2);
    const roundDigit = frac.length > 2 ? Number(frac[2]) : 0;
    const magnitude = Number(whole) * 100 + Number(centDigits) + (roundDigit >= 5 ? 1 : 0);
    if (!Number.isSafeInteger(magnitude))
        throw new AmountParseError(input);
    return sign * magnitude;
}
/**
 * Signierte Cent aus einem open-banking.io-Transaktionspaar.
 *
 * Wichtig: manche Banken liefern den Betrag bereits vorzeichenbehaftet, andere
 * immer positiv mit `creditDebitIndicator`. Wir normalisieren auf "Abgang ist
 * negativ" und verlassen uns dabei auf den Indikator, nicht auf das Vorzeichen.
 */
export function signedCents(amount, creditDebitIndicator) {
    const magnitude = Math.abs(toCents(amount));
    const indicator = (creditDebitIndicator ?? "").toUpperCase();
    if (indicator.startsWith("DBIT") || indicator === "DEBIT")
        return -magnitude;
    if (indicator.startsWith("CRDT") || indicator === "CREDIT")
        return magnitude;
    // Kein verwertbarer Indikator → dem Vorzeichen des Betrags vertrauen.
    return toCents(amount);
}
/** Formatiert Cent als deutschen Eurobetrag, z. B. -123456 → "-1.234,56 €". */
export function formatCents(cents, opts = {}) {
    const formatted = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        signDisplay: opts.sign ? "always" : "auto",
    }).format(cents / 100);
    return formatted;
}
/** Summiert Cent-Betraege. Existiert, damit nirgends versehentlich Floats addiert werden. */
export function sumCents(values) {
    let total = 0;
    for (const v of values)
        total += v;
    return total;
}
/**
 * Zerlegt einen Bruttobetrag in Netto und Steuer.
 *
 * Die Rundungsdifferenz landet bewusst im Netto, damit `netto + steuer` immer
 * exakt den Bruttobetrag ergibt — sonst laufen Summen ueber viele Positionen
 * auseinander.
 */
export function splitGross(grossCents, vatRatePercent) {
    if (vatRatePercent === 0)
        return { netCents: grossCents, vatCents: 0 };
    const vatCents = Math.round((grossCents * vatRatePercent) / (100 + vatRatePercent));
    return { netCents: grossCents - vatCents, vatCents };
}
/** Rechnet einen Nettobetrag auf brutto hoch. */
export function grossFromNet(netCents, vatRatePercent) {
    const vatCents = Math.round((netCents * vatRatePercent) / 100);
    return { grossCents: netCents + vatCents, vatCents };
}
//# sourceMappingURL=money.js.map